import { useState, useEffect } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { checkGrammar, groupIssues, loadSpellChecker, type EnglishVariant, type GrammarIssue, type SpellChecker } from "@/lib/grammarCheck";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

const TOOL_INFO_CONTENT = {
  what: `Checks English text for spelling mistakes, common grammar errors (don't/dont, their/they're, its/it's, "has went", a/an), spacing and capitalization, and fixes the clear ones. Mix-ups such as then/than, effect/affect and less/fewer are listed as suggestions. Anything it is not sure about is listed as a suggestion and left unchanged. Web addresses, e-mail addresses, file names and version numbers are never touched. Your text stays in your browser.`,
  how: `Paste your text and click Check Grammar. Read the corrected text and the list of fixes, then look through the suggestions it left for you to decide.`,
  tip: `This is a rule-based checker with a real English dictionary, not an AI. It reliably catches typos, contractions and common grammar slips, but it can't judge style or understand every sentence, so it lists doubtful cases as suggestions instead of changing them. English only. Correct UK and US spellings are never changed: choose English (UK) or English (US), or leave it on automatic to follow your text. A final read-through is still worth doing for anything important.`,
};

const SHOWN_GROUPS = 12;

/** Repeated identical messages become one line with a count; long lists stay short until opened. */
function IssueList({ list, label, testId }: { list: GrammarIssue[]; label: (n: number) => string; testId?: string }) {
  const [all, setAll] = useState(false);
  const groups = groupIssues(list);
  const visible = all ? groups : groups.slice(0, SHOWN_GROUPS);
  return (
    <div className="p-4 rounded-lg border bg-card space-y-1.5" data-testid={testId}>
      <p className="text-sm font-medium mb-1">{label(list.length)}</p>
      <div className={all ? "max-h-96 overflow-y-auto space-y-1.5" : "space-y-1.5"}>
        {visible.map((g, i) => (
          <p key={i} className="text-sm text-muted-foreground">• {g.message}{g.count > 1 ? ` (×${g.count})` : ""}</p>
        ))}
      </div>
      {groups.length > SHOWN_GROUPS && (
        <button type="button" className="text-sm text-primary underline" onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show all ${groups.length} kinds`}
        </button>
      )}
    </div>
  );
}

export default function GrammarChecker() {
  const { gateShare } = useShareGate();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [issues, setIssues] = useState<GrammarIssue[]>([]);
  const [checked, setChecked] = useState(false);
  const [suggestions, setSuggestions] = useState<GrammarIssue[]>([]);
  const [spell, setSpell] = useState<SpellChecker | null>(null);
  const [variant, setVariant] = useState<EnglishVariant>("auto");
  const [usedVariant, setUsedVariant] = useState<"us" | "uk">("us");
  useEffect(() => { loadSpellChecker().then(setSpell); }, []);

  const { logUsageNow } = useLogUsageOnAction();

  const check = async () => {
    if (!input.trim()) return;
    const allowed = await logUsageNow();
    if (!allowed) return;
    const localeHint = typeof navigator !== "undefined" ? navigator.language : undefined;
    const result = checkGrammar(input, spell ?? (await loadSpellChecker()), { variant, localeHint });
    setUsedVariant(result.variant ?? "us");
    setOutput(result.corrected);
    setIssues(result.issues);
    setSuggestions(result.suggestions);
    setChecked(true);
  };


  return (
    <ToolContainer>
      <div className="space-y-4">
        <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Enter text to check..." className="min-h-[150px]" />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={check} disabled={!input.trim()} className="gap-2">
            <Sparkles className="h-4 w-4" />
            Check Grammar
          </Button>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            English
            <select value={variant} onChange={(e) => setVariant(e.target.value as EnglishVariant)} className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground" data-testid="gc-variant">
              <option value="auto">Auto-detect</option>
              <option value="us">US spelling</option>
              <option value="uk">UK spelling</option>
            </select>
          </label>
        </div>

        {checked && (
          <div className="space-y-3">
            <div className="p-4 rounded-lg border bg-card">
              <p className="text-sm font-medium mb-2">Corrected text</p>
              <Textarea value={output} readOnly className="min-h-[120px] max-h-[360px]" />
              <Button size="sm" className="mt-2" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
            </div>

            {suggestions.length > 0 && (
              <IssueList list={suggestions} testId="gc-suggestions" label={(n) => `${n} suggestion${n > 1 ? "s" : ""} to review (not changed in the text)`} />
            )}
            {issues.length > 0 ? (
              <IssueList list={issues} testId="gc-fixes" label={(n) => `${n} fix${n > 1 ? "es" : ""} made`} />
            ) : (
              <div className="p-4 rounded-lg border bg-card flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" /> No clear mistakes found. This checker is rule-based, so a final read-through is still worth doing.
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Checked locally in your browser with an English dictionary and grammar rules, as {variant === "auto" ? `${usedVariant === "uk" ? "UK" : "US"} spelling (detected)` : `${usedVariant === "uk" ? "UK" : "US"} spelling`}. Correct UK and US spellings are never changed. It is not an AI checker and won't catch every mistake.
            </p>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
